import { useEffect, useMemo, useState } from "react";
import { HVTable } from "@repo/HVComponents";
import ECharts from "@components/common/chart";
import { EChartsOption } from "echarts";
import { useLang } from "@hooks/useLang";
import { useTheme } from "styled-components";
import { AnalyticsCategoryDataResponse } from "@queries/Analytics";
import _ from "lodash";
import { CategorySkeleton } from "src/pages/analytics/skeleton/CategorySkeleton";
import {
  ANALYTICS_CATEGORIES,
  ANALYTICS_CHART_TYPE
} from "@resources/Constants";
import * as S from "./CategoryChart.styled";
import { prepareOptions, categoryTypes } from "./PreviewData";
import { CategoryTab } from "../categoryTab";
import { fixAnalyticsValue, formatAnalyticsValue } from "../../chartUtils";

type IHVTable = React.ComponentProps<typeof HVTable>;

interface ICategoryChart {
  widgetType: string;
  categoryData: AnalyticsCategoryDataResponse;
  title?: string;
  updatedCategoryAnalytics?: (activeTab: string) => void;
  isCategoryDataLoading?: boolean;
  isReport?: boolean;
  categoryType?: string;
}

const CategoryChart = ({
  widgetType,
  categoryData,
  title,
  updatedCategoryAnalytics,
  isCategoryDataLoading,
  isReport = false,
  categoryType
}: ICategoryChart) => {
  const { translate } = useLang();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(
    isReport ? categoryType : categoryTypes.BY_HOURS
  );

  const CategoryOptions = [
    {
      label: translate("by_hour"),
      value: ANALYTICS_CATEGORIES.HOUR
    },
    {
      label: translate("by_day_of_the_week"),
      value: ANALYTICS_CATEGORIES.DAY
    }
  ];

  useEffect(() => {
    activeTab && updatedCategoryAnalytics?.(activeTab);
  }, [activeTab]);

  const isHourCategory = activeTab === categoryTypes.BY_HOURS;

  const getColumnsByWidgetType = (type: string): IHVTable["columns"] => {
    const categoryTypeColumn = {
      header: isHourCategory
        ? translate("hour")
        : translate("analytics_by_the_day_of_week"),
      accessorKey: "intervalName",
      size: 80,
      mantineTableBodyCellProps: {
        style: {
          justifyContent: "flex-start",
          textAlign: "left",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-primary)"
        }
      },
      mantineTableHeadCellProps: {
        style: {
          justifyContent: "flex-start",
          textAlign: "left",
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-secondary)",
          minWidth: "auto",
          width: "auto"
        }
      }
    };
    const rateColumn = {
      header: translate(`avg_per_${isHourCategory ? "hour" : "day"}`),
      accessorKey: "rate",
      size: 50,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({
        row
      }: {
        row: { original: { count: number; rate?: number } };
      }) => (
        <S.Text>
          {formatAnalyticsValue(row.original.count as number, type || "")} (
          {row.original.rate?.toFixed(1) || 0}%)
        </S.Text>
      ),
      mantineTableHeadCellProps: {
        style: {
          justifyContent: "flex-end",
          textAlign: "right",
          height: "32px",
          minHeight: "32px",
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-secondary)",
          minWidth: "auto",
          width: "auto"
        }
      },
      mantineTableBodyCellProps: {
        style: {
          height: "40px",
          minHeight: "40px",
          justifyContent: "flex-end",
          textAlign: "right",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-primary)"
        }
      }
    };
    const avgCount = {
      header: translate("avg_count"),
      accessorKey: "count",
      size: 30,
      mantineTableBodyCellProps: {
        style: {
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-primary)"
        }
      },
      mantineTableHeadCellProps: {
        style: {
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-secondary)"
        }
      },
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ row }: { row: { original: { count: number } } }) =>
        formatAnalyticsValue(row.original.count, type || "")
    };
    const waitTime = {
      header: translate("wait_time"),
      accessorKey: "time",
      size: 30,
      mantineTableBodyCellProps: {
        style: {
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-primary)"
        }
      },
      mantineTableHeadCellProps: {
        style: {
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "18px",
          color: "var(--hv-text-normal-secondary)"
        }
      },
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ row }: { row: { original: { time: number } } }) => {
        return `${Number(row.original.time?.toFixed(0) ?? row.original.time)}s`;
      }
    };

    const cumulativeType = [
      ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT,
      ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT,
      ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT
    ];

    if (cumulativeType.includes(type)) {
      return [categoryTypeColumn, rateColumn] as IHVTable["columns"];
    }

    const queueTypes = [
      ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
      ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES,
      ANALYTICS_CHART_TYPE.VEHICLE_QUEUES
    ];

    if (queueTypes.includes(type)) {
      return [categoryTypeColumn, avgCount, waitTime] as IHVTable["columns"];
    }

    return [categoryTypeColumn, rateColumn] as IHVTable["columns"];
  };

  const columns = useMemo<IHVTable["columns"]>(
    () => getColumnsByWidgetType(widgetType),
    [isHourCategory]
  );

  const chartOptions = useMemo(() => {
    const data: { value: number; intervalName: string; idx: number }[] = [];
    const labels: string[] = [];

    categoryData.forEach((item) => {
      data.push({
        value: fixAnalyticsValue(item.count, widgetType),
        intervalName: item.intervalName,
        idx: item.idx
      });
      labels.push(
        isHourCategory ? String(item.idx) : item.intervalName.slice(0, 3)
      );
    });

    return prepareOptions(labels, data, theme, isHourCategory);
  }, [isHourCategory, categoryData, JSON.stringify(theme)]);

  const highestValue = useMemo(() => {
    return _.maxBy(categoryData, (data) => data.count);
  }, [categoryData]);

  const getTranslationKey = () => {
    switch (widgetType) {
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
        return `analytics_category_Insight_cumulative_people_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
        return `analytics_category_Insight_people_occupancy_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
        return `analytics_category_Insight_people_queue_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
        return `analytics_category_Insight_cumulative_shoppingcart_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
        return `analytics_category_Insight_shoppingcart_in_use_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
        return `analytics_category_Insight_shoppingcart_queue_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
        return `analytics_category_Insight_cumulative_vehicle_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY:
        return `analytics_category_Insight_vehicle_occupancy_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
        return `analytics_category_Insight_vehicle_queue_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
        return `analytics_category_Insight_slip_and_fall_${
          isHourCategory ? "hour" : "day"
        }`;
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
        return `analytics_category_Insight_crowd_counting_${
          isHourCategory ? "hour" : "day"
        }`;

      default:
        return "analytics_no_data";
    }
  };
  const getTitleTranslationKey = () => {
    switch (widgetType) {
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
        return `analytics_slip_and_fall_by_category_title`;
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
        return `analytics_crowd_counting_by_category_title`;
      default:
        return "analytics_chart_by_category_title";
    }
  };

  const renderInsightStatement = () => {
    if (!highestValue) return null;
    const translationKey = getTranslationKey();
    let translation = translate(translationKey, {
      count: `<b>${formatAnalyticsValue(
        highestValue?.totalCount || highestValue.count,
        widgetType || ""
      )}</b>`,
      ...(isHourCategory
        ? {
            hourDuration: `<b>${highestValue.intervalName}</b>`
          }
        : {
            day: `<b>${highestValue.intervalName}</b>`
          }),
      ...(Number.isFinite(highestValue.time) && {
        time: `<b>${Number(highestValue.time?.toFixed(0))}(s)</b>`
      })
    });
    if (highestValue.totalCount === 0 || highestValue.count === 0) {
      translation = translate("no_data_this_period");
    }
    return (
      <S.StatementContainer>
        <S.Text secondary dangerouslySetInnerHTML={{ __html: translation }} />
      </S.StatementContainer>
    );
  };

  return (
    <S.CategoryChartContainer>
      <S.CategoryChartHeader>
        <S.HeaderText>
          {translate(getTitleTranslationKey(), {
            widgetType: title?.replace(/\bcount\b/, "")
          })}
        </S.HeaderText>
        {renderInsightStatement()}
      </S.CategoryChartHeader>

      <S.ChartBody>
        <S.TabsContainer>
          {!isReport && (
            <CategoryTab
              value={activeTab}
              onChange={setActiveTab}
              options={CategoryOptions}
            />
          )}
          {isCategoryDataLoading ? (
            <CategorySkeleton />
          ) : (
            <S.TabContent>
              <S.ChartTableContainer>
                <S.ChartTable>
                  {chartOptions && (
                    <ECharts
                      option={chartOptions as EChartsOption}
                      clearAfterUpdateChart
                      style={{ pointerEvents: "all", height: "320px" }}
                    />
                  )}
                </S.ChartTable>

                <S.ChartTable>
                  <HVTable
                    columns={columns}
                    hideSelectionColumn
                    data={categoryData}
                    tableOptions={{
                      initialState: {
                        sorting: [
                          ...(isHourCategory
                            ? [
                                {
                                  id: "intervalName",
                                  desc: false
                                }
                              ]
                            : [])
                        ]
                      },
                      enableSorting: true,
                      mantineTableBodyRowProps: {
                        style: {
                          fontWeight: 500,
                          fontSize: "12px",
                          lineHeight: "18px",
                          height: "40px",
                          minHeight: "40px",
                          color: "var(--hv-text-normal-primary)"
                        }
                      },
                      mantineTableHeadRowProps: {
                        style: {
                          height: "32px",
                          minHeight: "32px",
                          color: "var(--hv-text-normal-secondary)"
                        }
                      },
                      mantinePaperProps: {
                        style: { maxHeight: "320px", overflowY: "auto" }
                      }
                    }}
                  />
                </S.ChartTable>
              </S.ChartTableContainer>
            </S.TabContent>
          )}
        </S.TabsContainer>
      </S.ChartBody>
    </S.CategoryChartContainer>
  );
};

export default CategoryChart;
